"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { cacheCheckTTS, streamTTSText, synthesizeTTS } from "@/app/lib/api";
import { isFeatureEnabled } from "@/app/lib/clientConfig";
import type { TTSContext } from "@/app/lib/types";

// =============================================================================
// useTTSPlayer — Read-aloud playback state machine
// =============================================================================
//
// Single global <audio> element (kept in a module-level ref) so play /
// pause / seek survive card remounts and only one card plays at a time.
// Mirrors iOS TTSPlayer's "currentlyPlayingRecordId" behavior — at most
// one active (recordId, providerId) target per app.
//
// Cross-tab coordination via BroadcastChannel — when this tab starts
// playing, every other Trio tab receives a "tts-start" message and stops
// its own playback. iOS doesn't have this problem; web does.
//
// MediaSession API hooks lock-screen / control-center transport controls
// on mobile Safari so the user can play/pause/skip without unlocking.
// Desktop browsers expose the same handlers on the OS media keys.

export type TTSState =
  | "idle"
  | "fetching"
  | "playing"
  | "paused"
  | "seeking"
  | "error";

export interface TTSTarget {
  recordId: string;
  providerId: string;
  // The text we asked the server to synthesize — kept so a Retry on the
  // same target can re-fetch without re-derivation, and so the sentence
  // renderer can read the exact tokenization basis.
  text: string;
}

export interface UseTTSPlayerReturn {
  state: TTSState;
  /** The target currently playing OR paused. Null in idle/error. */
  active: TTSTarget | null;
  /** Audio currentTime in seconds, refreshed on `timeupdate`. */
  currentTime: number;
  /** Audio duration in seconds; 0 until metadata loads. */
  duration: number;
  /** Current playback rate. Cycles through SPEED_OPTIONS. */
  speed: number;
  /** Last error message; cleared on next play(). */
  error: string | null;

  play: (target: TTSTarget) => Promise<void>;
  pause: () => void;
  resume: () => void;
  /** Jump ±N seconds (clamped to [0, duration]). Negative = backward. */
  skip: (seconds: number) => void;
  /** Cycle through 1.0 → 1.25 → 1.5 → 1.75 → 2.0 → 1.0. */
  cycleSpeed: () => void;
  /** Jump to ~end and let the queued audio drain naturally. */
  skipToEnd: () => void;
  /** Stop, reset to idle, release blob URL. */
  stop: () => void;
}

const SPEED_OPTIONS = [1.0, 1.25, 1.5, 1.75, 2.0];
const BROADCAST_CHANNEL_NAME = "trio-tts";
const TTS_VOICE = "alloy";
const TTS_MODEL = "gpt-4o-mini-tts";
const TTS_CONTEXT: TTSContext = "one_off";

// MediaSource MIME type for the audio/mpeg streamed chunks.
const MSE_MIME = "audio/mpeg";

// Safari 17.1+ exposes ManagedMediaSource but TypeScript's DOM library does
// not yet declare it. Its playback surface matches MediaSource; its extra
// streaming-state events are advisory and do not control our append queue.
interface ManagedMediaSource extends MediaSource {}

interface ManagedMediaSourceConstructor {
  new (): ManagedMediaSource;
  isTypeSupported(type: string): boolean;
}

declare global {
  interface Window {
    ManagedMediaSource?: ManagedMediaSourceConstructor;
  }
}

// Module-level singleton so the hook returns the same audio element
// across component remounts. Two simultaneous useTTSPlayer mounts share
// the same playback — they're all views on the same player.
let _audioEl: HTMLAudioElement | null = null;
let _activeBlobURL: string | null = null;
// Active MediaSource (when streaming via /tts/stream-text). Held module-
// level so cleanup on stop()/play() can reliably tear it down even if
// the React component unmounted between operations.
let _activeMediaSource: MediaSource | ManagedMediaSource | null = null;
let _activeStreamAbortController: AbortController | null = null;

function getAudioEl(): HTMLAudioElement {
  if (typeof window === "undefined") {
    throw new Error("useTTSPlayer is client-only");
  }
  if (!_audioEl) {
    _audioEl = new Audio();
    _audioEl.preload = "auto";
  }
  return _audioEl;
}

function releaseBlobURL() {
  if (_activeBlobURL) {
    URL.revokeObjectURL(_activeBlobURL);
    _activeBlobURL = null;
  }
}

function tearDownMediaSource() {
  // Abort any in-flight /tts/stream-text fetch so server-side synth
  // can release its OpenAI connection promptly.
  _activeStreamAbortController?.abort();
  _activeStreamAbortController = null;
  const ms = _activeMediaSource;
  if (ms) {
    try {
      if (ms.readyState === "open") ms.endOfStream();
    } catch {
      // endOfStream throws if no source buffer was added or stream
      // already ended — both are fine to ignore during cleanup.
    }
    _activeMediaSource = null;
  }
}

function mediaSourceSupported(): boolean {
  if (typeof window === "undefined") return false;
  const MS = window.MediaSource ?? window.ManagedMediaSource;
  return typeof MS !== "undefined" && MS.isTypeSupported(MSE_MIME);
}

export function useTTSPlayer(): UseTTSPlayerReturn {
  const [state, setState] = useState<TTSState>("idle");
  const [active, setActive] = useState<TTSTarget | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const [error, setError] = useState<string | null>(null);

  // Active fetch's abort controller so a second play() supersedes the first
  // mid-flight rather than racing two audio loads against the same element.
  const fetchAbortRef = useRef<AbortController | null>(null);
  // Generation counter — incremented on every play() to invalidate stale
  // async resolutions that would otherwise overwrite a newer target's state.
  const genRef = useRef(0);

  // Wire audio element listeners ONCE per mount. Safe to call across many
  // simultaneous hook instances because removeEventListener with the same
  // captured ref is idempotent for inactive bindings.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = getAudioEl();

    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onLoadedMetadata = () => setDuration(isFinite(el.duration) ? el.duration : 0);
    const onPlay = () => setState("playing");
    const onPause = () => {
      // Pause events also fire when the audio ends (browser quirk). The
      // "ended" handler below will flip to idle; meanwhile if currentTime
      // is at end we leave state alone and let ended() take over.
      if (el.ended) return;
      setState("paused");
    };
    const onEnded = () => {
      setState("idle");
      setActive(null);
      setCurrentTime(0);
      releaseBlobURL();
      tearDownMediaSource();
      if (navigator.mediaSession) {
        navigator.mediaSession.playbackState = "none";
      }
    };
    const onError = () => {
      setState("error");
      setError(el.error?.message ?? "Audio playback failed");
    };

    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);

    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
  }, []);

  // Multi-tab coordination — when ANOTHER tab starts TTS, stop ours.
  // BroadcastChannel only delivers to other browsing contexts of the same
  // origin, so messages we post don't echo back to ourselves.
  useEffect(() => {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      if (e.data?.kind === "tts-start") {
        const el = getAudioEl();
        if (!el.paused) {
          el.pause();
          el.currentTime = 0;
          releaseBlobURL();
          tearDownMediaSource();
          setState("idle");
          setActive(null);
        }
      }
    };
    return () => channel.close();
  }, []);

  // Helper — after src is loaded onto the <audio> element, kick off
  // playback and set MediaSession metadata. Returns false on autoplay-
  // policy failure so the caller can surface it.
  const startPlayback = useCallback(async (target: TTSTarget): Promise<boolean> => {
    const el = getAudioEl();
    el.playbackRate = speed;
    try {
      await el.play();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState("error");
      setError(msg);
      setActive(null);
      return false;
    }
    if (typeof navigator !== "undefined" && navigator.mediaSession) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: "Trio response",
          artist: target.providerId,
          album: "Trio",
        });
        navigator.mediaSession.playbackState = "playing";
      } catch {
        // Older Firefox throws on MediaMetadata — ignore.
      }
    }
    return true;
  }, [speed]);

  const play = useCallback(async (target: TTSTarget) => {
    // A disabled TTS flag leaves the card control inert; do not enter the
    // fetching state or start a request that could leave a spinner running.
    if (!isFeatureEnabled("tts")) return;

    const gen = ++genRef.current;

    // Tear down ANY previous playback path (blob URL OR active stream).
    fetchAbortRef.current?.abort();
    fetchAbortRef.current = new AbortController();
    const el = getAudioEl();
    el.pause();
    tearDownMediaSource();
    releaseBlobURL();

    setState("fetching");
    setActive(target);
    setError(null);

    // Tell other tabs to stop their playback FIRST so the user-visible
    // "stop the other tab" is instant.
    if (typeof BroadcastChannel !== "undefined") {
      try {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channel.postMessage({ kind: "tts-start", recordId: target.recordId });
        channel.close();
      } catch {
        // Best-effort.
      }
    }

    const ttsRequest = {
      text: target.text,
      voice: TTS_VOICE,
      model: TTS_MODEL,
      context: TTS_CONTEXT,
    };

    // --- Phase 1: cache-only lookup. Cheap and non-billing — server's
    // /tts/cache-check returns bytes on hit, 404 on miss. iOS playing
    // the same card earlier warms the cache, so this is where the
    // iOS↔web sync win lands. ---
    let cachedBlob: Blob | null = null;
    try {
      cachedBlob = await cacheCheckTTS(ttsRequest);
    } catch (err) {
      // Network or auth failure on the cache check. Don't bail — fall
      // through to streaming so the user still gets audio. Log the
      // miss so debugging is possible.
      console.warn("[tts] cache-check failed; falling through to stream:", err);
    }
    if (gen !== genRef.current) return; // superseded

    if (cachedBlob) {
      // Cache hit — fast path. Use the blob directly.
      releaseBlobURL();
      _activeBlobURL = URL.createObjectURL(cachedBlob);
      el.src = _activeBlobURL;
      await startPlayback(target);
      return;
    }

    // --- Phase 2: cache miss → stream via /tts/stream-text. Use
    // MediaSource Extensions so the audio element starts playing as
    // soon as the first chunk arrives (~1-2s) instead of waiting for
    // the full synthesis (10-90s for long text). Falls back to a
    // simple blob fetch if MSE isn't supported. ---
    if (!mediaSourceSupported()) {
      // Fallback for very old browsers / environments where MSE is
      // missing. Slower first-audio (full synthesis wait) but works.
      try {
        const blob = await synthesizeTTS(ttsRequest, fetchAbortRef.current.signal);
        if (gen !== genRef.current) return;
        releaseBlobURL();
        _activeBlobURL = URL.createObjectURL(blob);
        el.src = _activeBlobURL;
        await startPlayback(target);
      } catch (err) {
        if (gen !== genRef.current) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState("error");
        setError(msg);
        setActive(null);
      }
      return;
    }

    // MediaSource path. Construct the MS, attach to audio, kick off
    // the streaming fetch, and pipe chunks into the source buffer as
    // they arrive. Playback starts at the first chunk's 'updateend'.
    const MS = window.MediaSource ?? window.ManagedMediaSource;
    if (!MS) {
      throw new Error("MediaSource became unavailable before streaming started");
    }
    const usesManagedMediaSource = window.MediaSource === undefined;
    const ms = new MS();
    _activeMediaSource = ms;
    releaseBlobURL();
    _activeBlobURL = URL.createObjectURL(ms);
    // ManagedMediaSource only opens when remote playback is disabled before
    // the source URL is assigned (Safari 17.1+).
    if (usesManagedMediaSource) el.disableRemotePlayback = true;
    el.src = _activeBlobURL;

    if (usesManagedMediaSource) {
      // These ManagedMediaSource events describe Safari's streaming state,
      // but are only hints. Fetch chunks remain the source of truth and are
      // appended by the same queue below regardless of either event.
      const ignoreStreamingHint = (_event: Event) => undefined;
      ms.addEventListener("startstreaming", ignoreStreamingHint);
      ms.addEventListener("endstreaming", ignoreStreamingHint);
    }

    // The streaming fetch's abort controller — separate from the
    // outer fetchAbortRef so stop()/new-play() teardown is precise.
    const streamAbort = new AbortController();
    _activeStreamAbortController = streamAbort;
    // Chain outer abort to stream abort so a generation bump from
    // stop()/new-play() also kills this stream.
    fetchAbortRef.current.signal.addEventListener(
      "abort",
      () => streamAbort.abort(),
      { once: true },
    );

    ms.addEventListener("sourceopen", async () => {
      if (gen !== genRef.current) return;
      let sourceBuffer: SourceBuffer;
      try {
        sourceBuffer = ms.addSourceBuffer(MSE_MIME);
      } catch (err) {
        if (gen !== genRef.current) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState("error");
        setError(msg);
        setActive(null);
        return;
      }

      // Queue of byte chunks waiting to be appended — appendBuffer
      // requires the source buffer's `updating` flag be false, so we
      // serialize via the 'updateend' event.
      const queue: Uint8Array[] = [];
      let streamDone = false;
      let firstChunkAppended = false;

      const drainQueue = () => {
        if (sourceBuffer.updating) return;
        const next = queue.shift();
        if (next) {
          try {
            // SourceBuffer.appendBuffer wants BufferSource. TS narrows
            // Uint8Array's buffer to ArrayBufferLike (could be
            // SharedArrayBuffer) but the DOM call accepts both at
            // runtime — cast through unknown to bridge the strict
            // typings.
            sourceBuffer.appendBuffer(next as unknown as BufferSource);
          } catch (err) {
            console.error("[tts] sourceBuffer.appendBuffer failed:", err);
          }
        } else if (streamDone) {
          // Stream finished and queue drained — close the source.
          try {
            if (ms.readyState === "open") ms.endOfStream();
          } catch {
            // Already closed.
          }
        }
      };
      sourceBuffer.addEventListener("updateend", () => {
        if (!firstChunkAppended) {
          firstChunkAppended = true;
          // Kick off playback now that we have at least one chunk
          // buffered. Subsequent chunks just keep the audio fed.
          if (gen === genRef.current) {
            void startPlayback(target);
          }
        }
        drainQueue();
      });

      // Now fire the streaming fetch and pipe bytes into the queue.
      try {
        const response = await streamTTSText(ttsRequest, streamAbort.signal);
        if (gen !== genRef.current) {
          // A newer play() superseded us — drop the response and
          // teardown was already triggered by the genRef bump.
          try { response.body?.cancel(); } catch { /* ignore */ }
          return;
        }
        if (!response.body) {
          throw new Error("Stream response has no body");
        }
        const reader = response.body.getReader();
        // Read loop: pull chunks, enqueue, drain.
        while (true) {
          const { value, done } = await reader.read();
          if (gen !== genRef.current) return;
          if (done) {
            streamDone = true;
            drainQueue();
            break;
          }
          if (value && value.byteLength > 0) {
            queue.push(value);
            drainQueue();
          }
        }
      } catch (err) {
        if (gen !== genRef.current) return;
        // Abort during teardown is expected; surface other errors.
        if (err instanceof DOMException && err.name === "AbortError") return;
        const msg = err instanceof Error ? err.message : String(err);
        setState("error");
        setError(msg);
        setActive(null);
      }
    }, { once: true });
  }, [startPlayback]);

  const pause = useCallback(() => {
    const el = getAudioEl();
    if (!el.paused) el.pause();
    if (navigator.mediaSession) navigator.mediaSession.playbackState = "paused";
  }, []);

  const resume = useCallback(() => {
    const el = getAudioEl();
    if (el.src && el.paused) {
      el.play().catch(() => {
        // Same autoplay-policy concern as above; user might need to tap
        // play again to satisfy the gesture requirement.
      });
      if (navigator.mediaSession) navigator.mediaSession.playbackState = "playing";
    }
  }, []);

  const skip = useCallback((seconds: number) => {
    const el = getAudioEl();
    if (!el.src) return;
    const target = Math.max(0, Math.min(el.duration || 0, el.currentTime + seconds));
    el.currentTime = target;
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeed((prev) => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
      getAudioEl().playbackRate = next;
      return next;
    });
  }, []);

  const skipToEnd = useCallback(() => {
    const el = getAudioEl();
    if (!el.src) return;
    // Jump near the end and let the buffered tail play. iOS does the
    // same — destructively stopping would skip the "ended" callbacks
    // some downstream consumers (cache writes etc.) depend on.
    el.currentTime = Math.max(0, (el.duration || 0) - 0.5);
    el.play().catch(() => {});
  }, []);

  const stop = useCallback(() => {
    genRef.current++;
    fetchAbortRef.current?.abort();
    const el = getAudioEl();
    if (!el.paused) el.pause();
    el.removeAttribute("src");
    el.load();
    releaseBlobURL();
    tearDownMediaSource();
    setState("idle");
    setActive(null);
    setCurrentTime(0);
    setDuration(0);
    if (navigator.mediaSession) {
      navigator.mediaSession.playbackState = "none";
      navigator.mediaSession.metadata = null;
    }
  }, []);

  // MediaSession action handlers wired ONCE per mount. Bound to the
  // latest callback identity via refs so the closures see the current
  // hook state. (Browsers persist handlers across page navigations as
  // long as the audio element is alive.)
  const playRef = useRef(play);
  const pauseRef = useRef(pause);
  const resumeRef = useRef(resume);
  const skipRef = useRef(skip);
  useEffect(() => { playRef.current = play; }, [play]);
  useEffect(() => { pauseRef.current = pause; }, [pause]);
  useEffect(() => { resumeRef.current = resume; }, [resume]);
  useEffect(() => { skipRef.current = skip; }, [skip]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    const ms = navigator.mediaSession;
    try {
      ms.setActionHandler("play", () => resumeRef.current());
      ms.setActionHandler("pause", () => pauseRef.current());
      ms.setActionHandler("seekbackward", () => skipRef.current(-15));
      ms.setActionHandler("seekforward", () => skipRef.current(30));
    } catch {
      // Some browsers don't support every action; ignore.
    }
    return () => {
      try {
        ms.setActionHandler("play", null);
        ms.setActionHandler("pause", null);
        ms.setActionHandler("seekbackward", null);
        ms.setActionHandler("seekforward", null);
      } catch {
        // Best-effort cleanup.
      }
    };
  }, []);

  return {
    state,
    active,
    currentTime,
    duration,
    speed,
    error,
    play,
    pause,
    resume,
    skip,
    cycleSpeed,
    skipToEnd,
    stop,
  };
}
